import { memo, useMemo } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheck, faPaperclip, faTimes, faXmark,
} from '@fortawesome/pro-light-svg-icons'

import { digObject, generateRedirectUrl } from '@domain-group/javascript-utils'

import { Link, Table, Text } from '@domain-group/suit-theme'

import { generateUrls } from '@hooks/useInvoice'

import type { InvoiceModel } from '@models/types'

import ApproveInvoiceButton from '../ApproveInvoiceButton'

export interface InvoicesTableProps {
  invoices: InvoiceModel[],
}

const buildColumns = () => [
  {
    title: 'Invoice #',
    dataKey: 'id',
    cellProps: {
      paddingLeft: 0,
      textAlign: 'center',
    },
    render: (id: number) => {
      const { editInvoiceUrl } = generateUrls({ id })

      return (
        <Link href={`${editInvoiceUrl}?redirect=${generateRedirectUrl()}`}>
          {id}
        </Link>
      )
    },
  },
  {
    title: 'Debtor',
    dataKey: 'debtor_name',
    cellProps: {
      minWidth: 250,
      style: { whiteSpace: 'nowrap' },
    },
  },
  {
    title: 'Orders',
    dataKey: 'approved_order_item_count',
    cellProps: {
      textAlign: 'center',
      width: 40,
    },
    render: (approved_order_item_count: number) => (
      <FontAwesomeIcon
        icon={approved_order_item_count > 0 ? faCheck : faXmark}
      />
    ),
  },
  {
    title: 'Reference',
    dataKey: 'reference',
    render: (reference: string) => (
      <Text variant="ellipsis">
        {reference}
      </Text>
    ),
  },
  {
    title: 'Title',
    dataKey: 'order_title',
    render: (order_title: string) => (
      <Text variant="ellipsis">
        {order_title}
      </Text>
    ),
  },
  {
    title: 'Currency',
    dataKey: 'currency_code',
    cellProps: {
      textAlign: 'center',
      width: 40,
    },
    render: (currency_code: string) => (
      <Text variant="ellipsis">
        {currency_code}
      </Text>
    ),
  },
  {
    title: 'Total',
    dataKey: 'total',
    cellProps: {
      width: 100,
    },
    render: (total: string) => `$${total}`,
  },
  {
    title: 'Paid',
    dataKey: 'paid',
    cellProps: {
      textAlign: 'center',
      width: 40,
    },
    render: (paid: number) => (
      <FontAwesomeIcon
        icon={paid ? faCheck : faXmark}
      />
    ),
  },
  {
    title: 'Approved',
    dataKey: 'approved',
    cellProps: {
      textAlign: 'center',
    },
    render: (approved: boolean) => <FontAwesomeIcon icon={approved ? faCheck : faTimes} />,
  },
  {
    title: 'Xero',
    dataKey: 'data',
    cellProps: {
      textAlign: 'center',
      width: 40,
    },
    render: (data?: object) => (
      <FontAwesomeIcon
        // @ts-expect-error TS2339
        icon={data?.xero_id ? faPaperclip : faXmark}
      />
    ),
  },
  {
    cellProps: {
      key: 'approveActionButton',
    },
    dataKey: 'id',
    render: (id: number) => <ApproveInvoiceButton invoiceId={id} />,
  },
]

const InvoicesTable = (props: InvoicesTableProps) => {
  const { invoices } = props

  const memoColumns = useMemo(() => (
    buildColumns()
  ), [invoices.length])

  return (
    <Table
      boxProps={{ border: 'none', maxHeight: 800, overflowY: 'auto' }}
      // @ts-expect-error TS2322
      columns={memoColumns}
      // @ts-expect-error TS2322
      data={invoices}
      stickyHeader
    />
  )
}

export default memo(InvoicesTable)
